import { useEffect, useState } from "react";
import { API, authFetch } from "@/App";
import { X, Download, Loader2 } from "lucide-react";

/**
 * Full-screen lightbox for protected /api/files/<path> images.
 * Renders a fixed overlay with the image, a download button, and a close button.
 */
export function AuthImageLightbox({ path, onClose, originalFilename }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let blobUrl = null;
    if (!path) return;
    (async () => {
      try {
        const res = await authFetch(`${API}/files/${path}`);
        if (!res.ok) return;
        const blob = await res.blob();
        blobUrl = URL.createObjectURL(blob);
        if (!cancelled) setSrc(blobUrl);
      } catch { /* swallow */ }
    })();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [path]);

  const download = () => {
    if (!src) return;
    const a = document.createElement("a");
    a.href = src;
    a.download = originalFilename || path.split("/").pop() || "attachment";
    a.click();
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="auth-image-lightbox"
    >
      <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
        <button
          onClick={(e) => { e.stopPropagation(); download(); }}
          className="px-3 py-1.5 rounded-md bg-white/15 hover:bg-white/25 text-white flex items-center gap-1.5 text-xs"
          data-testid="lightbox-download"
        >
          <Download className="w-3.5 h-3.5" /> Download
        </button>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md bg-white/15 hover:bg-white/25 text-white"
          data-testid="lightbox-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {src ? (
        <img
          src={src}
          alt="Preview"
          className="max-w-full max-h-full object-contain rounded shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      )}
    </div>
  );
}

export default AuthImageLightbox;
